// frontend/src/pages/admin/TicketStatuses.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Input, Popconfirm, Badge, Modal, Select } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import './users.css';
import TicketStatusForm from './TicketStatusForm';
import dayjs from 'dayjs';
import { useAppContext } from '../../context/AppContext';
import { useNotificationApi } from '../../context/NotificationContext';

const { Search } = Input;
const { Option } = Select;

const TicketStatuses = () => {
  const [ticketStatuses, setTicketStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTicketStatus, setEditingTicketStatus] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterFinalStatus, setFilterFinalStatus] = useState('all');

  const { appSettings } = useAppContext();
  const dateFormat = appSettings?.dateFormat || 'DD/MM/YYYY';
  const timeFormat = appSettings?.timeFormat || 'hh:mm A';

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

// In the fetchTicketStatuses function:
    const fetchTicketStatuses = useCallback(async () => {
    setLoading(true);
    const dismissLoading = showLoading('fetchStatuses', 'Loading ticket statuses...');
    try {
        let endpoint = '/api/ticket-statuses';
        // Updated query parameters to match backend
        const params = new URLSearchParams();
        
        if (filterFinalStatus === 'activeOnly') {
        params.append('finalOnly', 'false'); // Only non-final statuses
        } else if (filterFinalStatus === 'finalOnly') {
        params.append('finalOnly', 'true'); // Only final statuses
        }
        // No parameter needed for 'all' case
        
        const response = await api.get(`${endpoint}?${params.toString()}`);
        let fetchedStatuses = response.data.data;

        const filtered = searchText
        ? fetchedStatuses.filter(status =>
            status.name.toLowerCase().includes(searchText.toLowerCase())
            )
        : fetchedStatuses;

        const sorted = [...filtered].sort((a, b) => b.id - a.id);
        setTicketStatuses(sorted);
        showSuccess('Ticket Statuses Loaded', `Successfully fetched ${sorted.length} ticket statuses.`);
    } catch (error) {
        console.error('Failed to fetch ticket statuses:', error);
        const errorMessage = error.response?.data?.message || 'Error fetching ticket statuses.';
        const errorDetails = process.env.NODE_ENV === 'development' ? error.response?.data?.error : null;
        showError(
        'Loading Error',
        <span>
            {errorMessage}
            {errorDetails && <div>Details: {errorDetails}</div>}
        </span>,
        'fetchStatusesError'
        );
    } finally {
        setLoading(false);
        dismissLoading();
    }
    }, [searchText, filterFinalStatus, showLoading, showSuccess, showError]);

  useEffect(() => {
    fetchTicketStatuses();
  }, [fetchTicketStatuses]);

  const handleAddTicketStatus = () => {
    setEditingTicketStatus(null);
    setIsModalVisible(true);
  };

  const handleEditTicketStatus = (record) => {
    setEditingTicketStatus(record);
    setIsModalVisible(true);
  };

  const handleHardDeleteTicketStatus = async (id, name) => {
    setLoading(true);
    const dismissLoading = showLoading('deleteStatus', `Deleting "${name}"...`);
    try {
      await api.delete(`/api/ticket-statuses/${id}/hard-delete`);
      showSuccess('Status Deleted', `Ticket status "${name}" permanently deleted successfully.`);
      fetchTicketStatuses();
    } catch (error) {
      console.error('Failed to hard delete ticket status:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting ticket status.';
      const errorDetails = process.env.NODE_ENV === 'development' ? error.response?.data?.error : null;
      showError(
        'Deletion Failed',
        <span>
          {errorMessage}
          {errorDetails && <div>Details: {errorDetails}</div>}
        </span>,
        'deleteStatusError'
      );
    } finally {
      setLoading(false);
      dismissLoading();
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => b.id - a.id, // Sort by ID descending when clicked
      width: 80,
    },
    {
      title: 'Status Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      ellipsis: true,
    },
    {
      title: 'Is Final',
      dataIndex: 'isFinal',
      key: 'isFinal',
      render: (isFinal) => (
        <Badge
          status={isFinal ? 'success' : 'processing'} // Green for Final, Blue for Non-Final
          text={isFinal ? 'Final' : 'Non-Final'}
        />
      ),
      filters: [
        { text: 'Non-Final', value: false },
        { text: 'Final', value: true },
      ],
      onFilter: (value, record) => record.isFinal === value,
      width: 120,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => dayjs(text).format(`${dateFormat} ${timeFormat}`),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      width: 180,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text) => text ? dayjs(text).format(`${dateFormat} ${timeFormat}`) : 'N/A',
      sorter: (a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
      width: 180,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditTicketStatus(record)}
            aria-label={`Edit ${record.name}`}
          />
          <Popconfirm
            title={`Are you sure you want to permanently delete "${record.name}"?`}
            description="This action cannot be undone and may affect linked tickets. Proceed with caution."
            onConfirm={() => handleHardDeleteTicketStatus(record.id, record.name)}
            okText="Yes, Permanently Delete"
            cancelText="No, Keep It"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Hard Delete ${record.name}`}
            />
          </Popconfirm>
        </Space>
      ),
      width: 120,
    },
  ];

  return (
    <div className="ticket-statuses-page">
      <div className="ticket-statuses-header">
        <h2>Ticket Status Management</h2>
        <div className="ticket-statuses-actions">
          <Search
            placeholder="Search statuses..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={fetchTicketStatuses}
            style={{ width: 300 }}
          />
          <Select
            defaultValue="all"
            size="large"
            style={{ width: 180 }}
            value={filterFinalStatus}
            onChange={(value) => setFilterFinalStatus(value)}
          >
            <Option value="all">All Status</Option>
            <Option value="activeOnly">Non-Final Statuses</Option>
            <Option value="finalOnly">Final Statuses</Option>
          </Select>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddTicketStatus}
          >
            Add Status
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={ticketStatuses}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <TicketStatusForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={() => {
          setIsModalVisible(false);
          fetchTicketStatuses();
        }}
        ticketStatus={editingTicketStatus}
      />
    </div>
  );
};

export default TicketStatuses;